import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import DocViewer, {DocViewerRenderers} from '@cyntler/react-doc-viewer';
import {AppDispatch, RootState} from 'store/store';
import WhiteButton from '../components/WhiteButton';
import XIcon from '../assets/images/icons/XIcon';
import DownloadIcon from '../assets/images/icons/DownloadIcon';
import '@cyntler/react-doc-viewer/dist/index.css';
import {DocumentTemplate} from '../types/documentTemplate';
import DocumentTemplateRenderer from '../components/documentTemplate/DocumentTemplateRenderer';

interface UseViewerModalProps<_T> {
  openDrawer: () => void;
  fetchItemThunk: (type: string, id: string) => any;
  fetchDocumentTemplateThunk?: (args: any) => any;
}

const useViewerModal = <T,>({
  openDrawer,
  fetchItemThunk,
  fetchDocumentTemplateThunk,
}: UseViewerModalProps<T>) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [label, setLabel] = useState<string>('');
  const [selectedAttachmentUrl, setSelectedAttachmentUrl] = useState<
    string | null
  >(null);
  const [selectedItemType, setSelectedItemType] = useState<
    'file' | 'template' | null
  >(null);
  const [documentTemplateData, setDocumentTemplateData] = useState<any>(null);

  const dispatch = useDispatch<AppDispatch>();
  const company = useSelector((state: RootState) => state.company.company);

  const openModalForTemplate = (docData: any, label: string) => {
    const documentTemplate = new DocumentTemplate(docData.snapshot);
    setDocumentTemplateData(documentTemplate);
    setLabel(label);
    setSelectedItemType('template');
    setIsModalOpen(true);
  };

  const openModalForFile = (attachmentUrl: string, label: string) => {
    setSelectedAttachmentUrl(attachmentUrl);
    setLabel(label);
    setSelectedItemType('file');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAttachmentUrl(null);
    setSelectedItemType(null);
    setDocumentTemplateData(null);
    setLabel('');
  };
  const handleDownload = (attachmentUrl: string) => {
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    }
  };

  const handleNameClick = async (
    item: T & {
      type?: string;
      id: string;
      status: string;
      attachment?: {url?: string};
      name?: string;
      docType?: 'file' | 'template';
      recordType?: string;
    },
  ) => {
    if (item.docType === 'template') {
      if (fetchDocumentTemplateThunk) {
        const ownerType = item.recordType;
        const search = {
          where: {
            owner_id: {
              operator: '==',
              value: item.id,
            },
            owner_type: {
              operator: '==',
              value: `User${ownerType}`,
            },
          },
        };
        const response = await dispatch(
          fetchDocumentTemplateThunk({search: search}),
        );
        if (response.payload) {
          openModalForTemplate(response.payload.data[0], item.name || '');
        } else {
          alert('Could not fetch template data.');
        }
      } else {
        openDrawer();
      }
    } else {
      if (item.status === 'Complete' || item.attachment?.url != null) {
        if (item.attachment?.url) {
          openModalForFile(item.attachment.url, item.name || '');
        } else {
          alert('No attachment found.');
        }
      } else {
        await dispatch(fetchItemThunk(item.type || '', item.id));
        openDrawer();
      }
    }
  };

  const AttachmentModal = () => {
    if (!isModalOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={closeModal}>
        <div
          className="absolute bg-white rounded-xl w-4/6 py-6 h-[90vh] "
          onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-start gap-2 px-4">
            <div className="flex items-center gap-4">
              <div className="text-slate-600 text-xl font-semibold font-['Inter'] tracking-tight">
                {label}
              </div>
              {selectedItemType === 'file' && selectedAttachmentUrl && (
                <WhiteButton
                  onClick={() => handleDownload(selectedAttachmentUrl || '')}
                  icon={<DownloadIcon />}
                />
              )}
            </div>
            <button
              onClick={closeModal}
              className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
              <XIcon />
            </button>
          </div>
          <div className="flex p-4 h-full scroll-auto">
            {selectedItemType === 'file' && selectedAttachmentUrl && (
              <DocViewer
                pluginRenderers={DocViewerRenderers}
                documents={[{uri: selectedAttachmentUrl || ''}]}
                style={{height: '78h', overflow: 'auto'}}
                config={{
                  header: {
                    disableHeader: false,
                    disableFileName: true,
                    retainURLParams: false,
                  },
                  csvDelimiter: ',',
                  pdfVerticalScrollByDefault: true,
                }}
              />
            )}
            {selectedItemType === 'template' && documentTemplateData && (
              <div className="flex justify-center w-full overflow-auto">
                <DocumentTemplateRenderer
                  template={documentTemplateData}
                  mode="view"
                  companyInfo={{
                    logoUrl: company.logoUrl,
                    name: company.name,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return {
    openModalForFile,
    closeModal,
    AttachmentModal,
    handleNameClick,
  };
};

export default useViewerModal;
